import apiService from './api.service';

export interface BillingSystemStatus {
  cron: {
    total: number;
    running: number;
    jobs: Array<{ name: string; running: boolean; nextRun: string }>;
  } | null;
  emailQueue: { pending: number; sent: number; failed: number; dead: number };
  invoices: {
    pending: number;
    overdue: number;
    uncollectible: number;
    in_retry: number;
    missing_pdf: number;
  };
  dunningQueue: Array<{
    id: string;
    invoice_number: string;
    status: string;
    retry_attempts: number;
    last_retry_at: string | null;
    next_retry_at: string | null;
    last_retry_error: string | null;
    amount_due: number;
    currency: string;
  }>;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  slug: string;
  description: string;
  tier?: 'free' | 'starter' | 'pro' | 'enterprise';
  price_monthly: number;
  price_annual: number;
  currency: string;
  max_applications: number;
  max_users: number;
  max_hosts: number;
  data_retention_days: number;
  included_metrics_events: number;
  included_log_events: number;
  included_trace_spans: number;
  included_storage_gb: number;
  // Cloud Scans
  included_cloud_scans?: number;
  price_per_cloud_scan?: number;
  // Uptime Monitors
  included_uptime_monitors?: number;
  price_per_uptime_monitor?: number;
  // Security Scans
  included_security_scans?: number;
  price_per_security_scan?: number;
  features: string[];
  is_active: boolean;
  is_public: boolean;
  sort_order?: number;
}

export interface Subscription {
  id: string;
  tenant_id: string;
  plan_id: string;
  plan: SubscriptionPlan;
  tenant?: {
    id: string;
    name: string;
    currency: 'USD' | 'BRL';
  };
  status: string;
  billing_cycle: 'MONTHLY' | 'ANNUAL';
  current_period_start: string;
  current_period_end: string;
  trial_ends_at: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

export interface Invoice {
  id: string;
  invoice_number: string;
  status: string;
  subtotal: number;
  tax: number;
  total: number;
  amount_paid: number;
  amount_due: number;
  currency: string;
  issued_at: string;
  due_at: string;
  paid_at: string | null;
  stripe_hosted_invoice_url: string | null;
  stripe_invoice_pdf: string | null;
}

export interface PaymentMethod {
  id: string;
  type: string;
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
}

export interface UsageData {
  current_period_start: string;
  current_period_end: string;
  metrics_events: number;
  log_events: number;
  trace_spans: number;
  storage_gb: number;
  metrics_overage: number;
  logs_overage: number;
  traces_overage: number;
  storage_overage: number;
  overage_costs: {
    metrics: number;
    logs: number;
    traces: number;
    storage: number;
    total: number;
  };
}

class BillingApi {
  // ========== PLANS ==========
  async getPublicPlans() {
    return apiService.get<{ success: boolean; data: SubscriptionPlan[] }>('/v1/billing/plans');
  }

  // ========== SUBSCRIPTIONS ==========
  async getCurrentSubscription() {
    return apiService.get<{ success: boolean; data: Subscription }>('/v1/billing/subscriptions/current');
  }

  async createSubscription(data: { planId: string; billingCycle: 'MONTHLY' | 'ANNUAL' }) {
    return apiService.post<{ success: boolean; data: { sessionUrl: string } }>('/v1/billing/subscriptions', data);
  }

  async changePlan(subscriptionId: string, data: { newPlanId: string; billingCycle?: 'MONTHLY' | 'ANNUAL' }) {
    return apiService.post<{ success: boolean; data: Subscription }>(
      `/v1/billing/subscriptions/${subscriptionId}/change-plan`,
      data
    );
  }

  async cancelSubscription(subscriptionId: string) {
    return apiService.post<{ success: boolean; data: Subscription }>(
      `/v1/billing/subscriptions/${subscriptionId}/cancel`,
      {}
    );
  }

  async resumeSubscription(subscriptionId: string) {
    return apiService.post<{ success: boolean; data: Subscription }>(
      `/v1/billing/subscriptions/${subscriptionId}/resume`,
      {}
    );
  }

  // ========== INVOICES ==========
  async getInvoices(params?: { limit?: number; offset?: number }) {
    return apiService.get<{ success: boolean; data: { invoices: Invoice[]; total: number } }>('/v1/billing/invoices', {
      params,
    });
  }

  async getInvoice(invoiceId: string) {
    return apiService.get<{ success: boolean; data: Invoice }>(`/v1/billing/invoices/${invoiceId}`);
  }

  // ========== PAYMENT METHODS ==========
  async getPaymentMethods() {
    return apiService.get<{ success: boolean; data: PaymentMethod[] }>('/v1/billing/payment-methods');
  }

  async updatePaymentMethod(paymentMethodId: string) {
    return apiService.post<{ success: boolean; message: string }>('/v1/billing/payment-method', {
      paymentMethodId,
    });
  }

  // ========== USAGE ==========
  async getCurrentUsage(params?: { startDate?: string; endDate?: string }) {
    return apiService.get<{ success: boolean; data: UsageData }>('/v1/billing/usage/current', { params });
  }

  async getUsageHistory(params?: { startDate?: string; endDate?: string }) {
    return apiService.get<{ success: boolean; data: UsageData[] }>('/v1/billing/usage/history', { params });
  }

  async checkQuota() {
    return apiService.get<{
      success: boolean;
      data: {
        metrics_remaining: number;
        logs_remaining: number;
        traces_remaining: number;
        storage_remaining: number;
        is_over_quota: boolean;
      };
    }>('/v1/billing/usage/quota');
  }

  // ========== PAY-AS-YOU-GO USAGE ==========
  async getPayAsYouGoUsage(params?: { startDate?: string; endDate?: string }) {
    return apiService.get<{
      success: boolean;
      data: {
        usage: {
          avgInfraHosts: number;
          avgAPMHosts: number;
          spanIngestionGB: number;
          indexedSpansCount: number;
          logIngestionGB: number;
          indexedLogEventsCount: number;
          customMetricsCount: number;
          rumSessionsCount: number;
          rumEventsCount: number;
          sessionRecordingsCount: number;
          recordingStorageGB: number;
        };
        costs: {
          baseCost: number;
          infraHostsCost: number;
          apmHostsCost: number;
          spanIngestionCost: number;
          indexedSpansCost: number;
          logIngestionCost: number;
          indexedLogsCost: number;
          customMetricsCost: number;
          rumSessionsCost: number;
          rumEventsCost: number;
          sessionRecordingsCost: number;
          recordingStorageCost: number;
          infraHostsUsed: number;
          infraHostsIncluded: number;
          infraHostsBillable: number;
          apmHostsUsed: number;
          apmHostsIncluded: number;
          apmHostsBillable: number;
          spanIngestionUsedGB: number;
          spanIngestionIncludedGB: number;
          spanIngestionBillableGB: number;
          indexedSpansUsed: number;
          indexedSpansIncluded: number;
          indexedSpansBillable: number;
          logIngestionUsedGB: number;
          logIngestionIncludedGB: number;
          logIngestionBillableGB: number;
          indexedLogsUsed: number;
          indexedLogsIncluded: number;
          indexedLogsBillable: number;
          customMetricsUsed: number;
          customMetricsIncluded: number;
          customMetricsBillable: number;
          rumSessionsUsed: number;
          rumSessionsIncluded: number;
          rumSessionsBillable: number;
          rumEventsUsed: number;
          rumEventsIncluded: number;
          rumEventsBillable: number;
          sessionRecordingsUsed: number;
          sessionRecordingsIncluded: number;
          sessionRecordingsBillable: number;
          recordingStorageUsedGB: number;
          recordingStorageIncludedGB: number;
          recordingStorageBillableGB: number;
          totalCost: number;
        };
        plan: {
          id: string;
          name: string;
          tier: string;
          price_monthly: number;
          price_annual: number;
        };
        subscription: {
          id: string;
          status: string;
          billing_cycle: string;
          trial_ends_at: string | null;
          current_period_start: string;
          current_period_end: string;
        };
      };
    }>('/v1/billing/usage/payg', { params });
  }

  async getUsageHistoryPayg(months: number = 6) {
    return apiService.get<{ success: boolean; data: any[] }>(
      `/v1/billing/usage/history-payg?months=${months}`
    );
  }

  async getDailyCosts(params?: { startDate?: string; endDate?: string }) {
    return apiService.get<{
      success: boolean;
      data: Array<{
        date: string;
        cost: number;
        breakdown: {
          metrics: number;
          logs: number;
          traces: number;
          rumSessions: number;
          rumEvents: number;
          recordings: number;
          recordingStorage: number;
          cloudScans: number;
        };
      }>;
    }>('/v1/billing/usage/daily-costs', { params });
  }

  // ========== STRIPE PORTAL ==========
  async createPortalSession() {
    return apiService.post<{ success: boolean; data: { url: string } }>('/v1/billing/portal', {});
  }

  // ========== ADMIN: PLAN MANAGEMENT ==========
  async getAllPlans(includeInactive: boolean = false) {
    return apiService.get<{ success: boolean; data: SubscriptionPlan[] }>(
      `/v1/billing/admin/plans?includeInactive=${includeInactive}`
    );
  }

  async getPlanById(planId: string) {
    return apiService.get<{ success: boolean; data: SubscriptionPlan }>(`/v1/billing/admin/plans/${planId}`);
  }

  async createPlan(data: Omit<SubscriptionPlan, 'id' | 'created_at' | 'updated_at'>) {
    return apiService.post<{ success: boolean; data: SubscriptionPlan; message: string }>(
      '/v1/billing/admin/plans',
      data
    );
  }

  async updatePlan(planId: string, data: Partial<SubscriptionPlan>) {
    return apiService.put<{ success: boolean; data: SubscriptionPlan; message: string }>(
      `/v1/billing/admin/plans/${planId}`,
      data
    );
  }

  async deletePlan(planId: string) {
    return apiService.delete<{ success: boolean; message: string }>(`/v1/billing/admin/plans/${planId}`);
  }

  async reorderPlans(orders: Array<{ id: string; sort_order: number }>) {
    return apiService.put<{ success: boolean; message: string }>('/v1/billing/admin/plans/reorder', { orders });
  }

  async syncPlanWithStripe(planId: string) {
    return apiService.post<{ success: boolean; data: SubscriptionPlan; message: string }>(
      `/v1/billing/admin/plans/${planId}/sync-stripe`,
      {}
    );
  }

  // ---- System status / dunning / backfill (admin) ----
  async getSystemStatus() {
    return apiService.get<{ success: boolean; data: BillingSystemStatus }>(
      '/v1/billing/admin/system-status'
    );
  }

  async runInvoiceGeneration() {
    return apiService.post<{ success: boolean; data: { generated: number; skipped: number; failed: number } }>(
      '/v1/billing/admin/run-invoice-generation',
      {}
    );
  }

  async runDunning() {
    return apiService.post<{ success: boolean; data: { retried: number; failed: number; suspended: number } }>(
      '/v1/billing/admin/run-dunning',
      {}
    );
  }

  async regenerateInvoicePdf(invoiceId: string) {
    return apiService.post<{ success: boolean; data: Invoice }>(
      `/v1/billing/admin/invoices/${invoiceId}/regenerate-pdf`,
      {}
    );
  }
}

export default new BillingApi();
